import React, { FC } from 'react';
import { GhostButton } from 'src/components/widgets/buttons';
import { Icon } from 'src/components/widgets/icon';
import { Input } from 'src/components/widgets/input';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { T } from 'src/shared/helpers';
import EditIcon from 'src/shared/material_icons/image/edit.svg';
import styled from 'styled-components';
import { useInlineRename } from '../aiRulesShared/hooks/useInlineRename';

const NameText = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
`;

const EditButton = styled(GhostButton)`
    opacity: 0;
    transition: opacity 0.15s ease;
`;

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;

    &:hover ${EditButton}, &:focus-within ${EditButton} {
        opacity: 1;
    }
`;

const EmptyPlaceholder = styled.span`
    color: ${(props) => props.theme.app.primary};
    opacity: 0.75;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        opacity: 1;
        text-decoration: underline;
    }
`;

const InlineInput = styled(Input)`
    width: 100%;
    max-width: 200px;
`;

interface InlineRenameCellProps {
    name: string;
    onRename: (newName: string) => void;
    emptyLabel?: string;
}

const InlineRenameCell: FC<InlineRenameCellProps> = ({ name, onRename, emptyLabel = T._('Add a name', 'Admin') }) => {
    const { isEditing, draft, setDraft, inputRef, startEditing, commit, handleKeyDown } = useInlineRename(name, onRename);
    const actionLabel = name ? T._('Rename', 'Admin') : emptyLabel;

    if (isEditing) {
        return (
            <InlineInput
                ref={inputRef}
                value={draft}
                maxLength={255}
                placeholder={T._('Enter a name', 'Admin')}
                onChange={(e) => setDraft(e.currentTarget.value)}
                onKeyDown={handleKeyDown}
                onBlur={commit}
            />
        );
    }

    return (
        <Wrapper>
            {name ? (
                <ZTooltip position='top' title={name}>
                    <NameText onDoubleClick={startEditing}>{name}</NameText>
                </ZTooltip>
            ) : (
                <EmptyPlaceholder onClick={startEditing}>{emptyLabel}</EmptyPlaceholder>
            )}
            <ZTooltip position='top' title={actionLabel}>
                <EditButton $size='small' $type='icon' aria-label={actionLabel} onClick={startEditing}>
                    <Icon>
                        <EditIcon />
                    </Icon>
                </EditButton>
            </ZTooltip>
        </Wrapper>
    );
};

export default InlineRenameCell;
